"use client";

import React from "react";

/** Mirrors Base UI's `InteractionType`, which it does not re-export from a package
 *  we depend on directly. */
type OpenType = "mouse" | "touch" | "pen" | "keyboard" | "";

const FORM_FIELD_SELECTOR =
  'input:not([type="hidden"]), select, textarea, [role="combobox"]';

const NON_TYPABLE_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isTypable(field: HTMLElement) {
  if (field instanceof HTMLInputElement) {
    return !NON_TYPABLE_INPUT_TYPES.has(field.type) && !field.readOnly;
  }

  if (field instanceof HTMLTextAreaElement) return !field.readOnly;

  return true;
}

function isHiddenByStyle(element: Element) {
  const style = getComputedStyle(element);

  return (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.visibility === "collapse" ||
    style.opacity === "0"
  );
}

/**
 * Whether anything between the field and the modal container hides it. Walks
 * ancestors because computed style only ever describes a single node.
 *
 * The container itself is deliberately excluded: a modal commonly fades in over
 * a few hundred milliseconds, so its own opacity still reads as 0 at the moment
 * we look. Whether the modal is up is the caller's business, not ours.
 */
function isHiddenWithin(field: HTMLElement, container: HTMLElement) {
  for (
    let node: Element | null = field;
    node && node !== container;
    node = node.parentElement
  ) {
    if (isHiddenByStyle(node)) return true;
  }

  return false;
}

/** First field a user could actually type into, in DOM order. Skips the spam
 *  honeypot, non-current steps of a multi-step form, and fields still hidden
 *  behind a conditional reveal. */
export function findFirstVisibleField(container: HTMLElement | null) {
  if (!container) return null;

  const fields = Array.from(
    container.querySelectorAll<HTMLElement>(FORM_FIELD_SELECTOR),
  );

  return (
    fields.find(
      (field) =>
        field.tabIndex >= 0 &&
        !field.matches(":disabled") &&
        isTypable(field) &&
        field.closest('[hidden], [inert], [aria-hidden="true"]') === null &&
        !isHiddenWithin(field, container),
    ) ?? null
  );
}

/** Written to `data-autofocus` on the container so QA can see what the hook
 *  resolved without stepping through it. */
function describeField(field: HTMLElement | null) {
  if (!field) return "none";

  const name = field.getAttribute("name") || field.id;

  return name ? `${field.localName}[${name}]` : field.localName;
}

/** Whether this open should move focus to a field at all. Exported for modals that
 *  need their own fallback when there is no field — most callers want the hook. */
export function shouldAutofocus(openType?: OpenType) {
  if (openType === "touch" || openType === "pen") return false;
  if (typeof window.matchMedia !== "function") return false;

  return window.matchMedia("(pointer: fine)").matches;
}

/**
 * Opens a modal with its first field focused, so a form is ready to type into.
 * Put `contentRef` on the element wrapping the modal's children, then wire up
 * whichever of the two modes fits.
 *
 * For a Base UI dialog, pass `initialFocus` to the popup. Base UI's own default
 * focuses the first tabbable element, which in our `Popup` is always the close
 * button; returning `true` defers to it. That happens when the open came from a
 * touch or pen, when the device has no fine pointer, and when there is no field
 * worth focusing. The first two are separate cases — a hybrid laptop reports a
 * fine pointer even when the user tapped, and focusing then raises the keyboard.
 *
 * For a hand-rolled modal with no focus manager — or a plain page form that opted
 * in via `Form.Root`'s `autoFocusFirstField` — pass `active` instead and the hook
 * moves focus itself once the container is up. Do NOT use this mode with a Base UI
 * dialog: an effect here would land before Base UI's own focus handling and be
 * overwritten by it.
 */
export function useAutofocusFirstField({ active }: { active?: boolean } = {}) {
  const [content, setContent] = React.useState<HTMLElement | null>(null);

  const initialFocus = React.useCallback(
    (openType: OpenType) => {
      if (!shouldAutofocus(openType)) {
        if (content)
          content.dataset.autofocus = `skipped-${openType || "unknown"}`;
        return true;
      }

      const field = findFirstVisibleField(content);
      if (content) content.dataset.autofocus = describeField(field);

      return field ?? true;
    },
    [content],
  );

  React.useEffect(() => {
    if (!active || !content) return;

    if (!shouldAutofocus()) {
      content.dataset.autofocus = "skipped-coarse-pointer";
      return;
    }

    const field = findFirstVisibleField(content);
    field?.focus();
    content.dataset.autofocus = describeField(field);
  }, [active, content]);

  return { contentRef: setContent, initialFocus };
}
